import {
	AfterViewInit,
	ComponentRef,
	Directive,
	ElementRef,
	Injector,
	OnDestroy,
	ViewContainerRef,
	effect,
	inject,
	input,
	output
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { KeyboardComponent } from './keyboard.component';
import { KeyboardStepMode } from './keyboard.model';
import { KeyboardService } from './keyboard.service';
import { parseClipboardNumber } from './keyboard.tools';

/**
 * Opens {@link KeyboardComponent} when the host control is clicked.
 * Place on the same element as `ngModel` / `formControl` (e.g. `ui-input`, `ui-input-number`).
 */
@Directive({
	selector: '[appKeyboard]',
	host: {
		'(click)': 'onHostClick($event)',
		'(paste)': 'onPaste($event)',
		'(keydown)': 'onKeydown($event)',
		'(document:click)': 'onDocumentClick($event)'
	}
})
export class KeyboardDirective implements AfterViewInit, OnDestroy {
	readonly stepMode = input<KeyboardStepMode>(KeyboardStepMode.Amount);
	readonly stepValue = input<string | number>('1');
	readonly positionFixed = input(true);
	/** When false, the custom keyboard will not open. */
	readonly keyboardEnabled = input(true);

	readonly keyboardOpened = output<boolean>();

	private readonly el = inject(ElementRef<HTMLElement>);
	private readonly viewContainerRef = inject(ViewContainerRef);
	private readonly injector = inject(Injector);
	private readonly control = inject(NgControl);
	private readonly keyboardService = inject(KeyboardService);

	private inputEl: HTMLInputElement | null = null;
	private overlayRef: HTMLElement | null = null;
	private valueSub?: Subscription;
	private componentRef?: ComponentRef<KeyboardComponent>;

	constructor() {
		effect(() => {
			if (!this.keyboardEnabled()) {
				this.destroyOverlay();
			}
		});
	}

	ngAfterViewInit(): void {
		// Defer so projected / inner inputs from CVA hosts are in the DOM.
		setTimeout(() => {
			this.inputEl = this.resolveInputElement();
			// The field must stay editable for the OS to offer «چسباندن» on long press —
			// a `readonly` input never gets a paste menu. `inputmode="none"` keeps the
			// native keyboard down so only the custom one shows.
			this.inputEl?.setAttribute('inputmode', 'none');
		});
	}

	onHostClick(event: MouseEvent): void {
		const target = event.target as HTMLElement | null;
		// Ignore accessory controls (lock, min/max, calculator) inside the field.
		if (target?.closest('button, ui-button, a, [role="button"]')) {
			return;
		}
		this.openKeyboard();
	}

	/**
	 * Native paste (long press → «چسباندن», or Ctrl+V). The field carries a formatted number,
	 * so the clipboard text is normalised and written to the control instead of being dropped
	 * into the input verbatim; text without a number leaves the value untouched.
	 */
	onPaste(event: ClipboardEvent): void {
		const numeric = parseClipboardNumber(event.clipboardData?.getData('text') ?? '');
		event.preventDefault();

		if (!numeric) {
			return;
		}

		const value = Number(numeric);
		this.control.control?.setValue(value);
		this.control.viewToModelUpdate(value);
	}

	/**
	 * A hardware keyboard (desktop) types through the custom one, so the field stays `readonly`
	 * and every value still takes the keypad's path: digits (Persian and Arabic too) are pressed,
	 * Backspace / Delete erase, Enter confirms and Escape cancels. A field whose text is wholly
	 * selected is replaced rather than appended to, as a native input would.
	 */
	onKeydown(event: KeyboardEvent): void {
		if (!this.keyboardEnabled() || event.ctrlKey || event.metaKey || event.altKey) {
			return;
		}

		const digit = event.key.length === 1 ? parseClipboardNumber(event.key) : '';
		const erase = event.key === 'Backspace' || event.key === 'Delete';
		if (digit || erase) {
			event.preventDefault();
			this.openKeyboard();
			const keyboard = this.componentRef?.instance;
			if (!keyboard) {
				return;
			}
			if (this.isWhollySelected()) {
				keyboard.setValue(digit);
			} else if (digit) {
				keyboard.press(digit);
			} else {
				keyboard.delete();
			}
			return;
		}

		const keyboard = this.componentRef?.instance;
		if (event.key === 'Enter' && keyboard) {
			event.preventDefault();
			keyboard.confirm();
		} else if (event.key === 'Escape' && keyboard) {
			event.preventDefault();
			keyboard.cancel();
		}
	}

	onDocumentClick(event: MouseEvent): void {
		if (!this.overlayRef || !this.inputEl) {
			return;
		}
		const target = event.target as Node;
		const host = this.el.nativeElement;
		if (!host.contains(target) && !this.overlayRef.contains(target)) {
			this.destroyOverlay();
		}
	}

	openKeyboard(): void {
		if (!this.keyboardEnabled()) {
			return;
		}
		this.inputEl ??= this.resolveInputElement();
		if (!this.inputEl) {
			return;
		}
		this.keyboardService.requestOpen(this);
		this.inputEl.focus();
		this.showOverlay();
	}

	/** Called by {@link KeyboardService} when another field takes over the keyboard. */
	closeKeyboard(): void {
		this.destroyOverlay();
	}

	ngOnDestroy(): void {
		this.destroyOverlay();
		this.valueSub?.unsubscribe();
		this.inputEl = null;
	}

	private resolveInputElement(): HTMLInputElement | null {
		const host = this.el.nativeElement;
		if (host instanceof HTMLInputElement) {
			return host;
		}
		return host.querySelector('input');
	}

	private isWhollySelected(): boolean {
		const input = this.inputEl;
		return !!input?.value && input.selectionStart === 0 && input.selectionEnd === input.value.length;
	}

	private toKeyboardValue(value: unknown): string {
		if (!String(value)?.length) {
			return '';
		}
		return String(value).replace(/,/g, '');
	}

	private showOverlay(): void {
		if (!this.keyboardEnabled() || this.overlayRef || !this.inputEl) {
			return;
		}

		const componentRef = this.viewContainerRef.createComponent(KeyboardComponent, {
			injector: this.injector
		});
		this.componentRef = componentRef;
		const keyboard = componentRef.instance;

		keyboard.setValue(this.toKeyboardValue(this.control.control?.value));
		keyboard.stepMode.set(this.stepMode());
		keyboard.stepValue.set(this.stepValue());
		keyboard.positionFixed.set(this.positionFixed());

		keyboard.valueChange.subscribe((val: string) => {
			// Keep clear/backspace as empty so hosts can show blank vs explicit 0.
			if (val === '') {
				this.control.control?.setValue('');
				this.control.viewToModelUpdate('');
				return;
			}
			const parsed = Number(val);
			const nextValue = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
			this.control.control?.setValue(nextValue);
			this.control.viewToModelUpdate(nextValue);
		});

		this.valueSub?.unsubscribe();
		this.valueSub = this.control.control?.valueChanges.subscribe(value => {
			keyboard.value.set(this.toKeyboardValue(value));
		});

		keyboard.confirmEvent.subscribe(() => this.destroyOverlay());
		keyboard.cancelEvent.subscribe(() => this.destroyOverlay());

		const domElem = componentRef.location.nativeElement as HTMLElement;
		document.body.appendChild(domElem);
		this.overlayRef = domElem;

		this.keyboardService.setVisibility(true);
		this.keyboardOpened.emit(true);
	}

	private destroyOverlay(): void {
		if (!this.overlayRef) {
			return;
		}

		const overlay = this.overlayRef;
		const componentRef = this.componentRef;
		this.overlayRef = null;
		this.componentRef = undefined;
		this.valueSub?.unsubscribe();
		this.valueSub = undefined;
		this.keyboardOpened.emit(false);
		this.keyboardService.clearActive(this);
		this.keyboardService.setVisibility(false);

		componentRef?.destroy();
		overlay.remove();
	}
}
